import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {Cancelable} from 'lodash';
import {debounce} from 'lodash-es';
import {BehaviorSubject, combineLatest, Observable, of, Subject, Subscription} from 'rxjs';
import {catchError, map, switchMap, tap} from 'rxjs/operators';
import {environment} from '../../../environments/environment';
import {cache, KEY_ENTER, KEY_ESC} from '../../../utils';
import {AnalyticsService} from '../../shared/analytics.service';
import {HttpError} from '../../shared/api.service';
import {AuthService} from '../../shared/auth.service';
import {ModalComponent} from '../../shared/modal/modal.component';
import {NavigationService} from '../../shared/navigation.service';
import {ClientApiService} from '../client-api.service';
import {TermsModalComponent} from '../terms-modal/terms-modal.component';
import {TimecardFilterSortOptions, TimecardFilterSortPipe} from '../timecard-filter-sort.pipe';

@Component({
  selector: 'timecards-page',
  templateUrl: './timecards-page.component.html',
  styleUrls: ['../styles/paginator.scss', '../styles/search-sort.scss', './timecards-page.component.scss']
})
export class TimecardsPageComponent implements OnInit, OnDestroy {

  disputedTab = environment.flags.timecardsDisputedTab;

  private timecardsSub: Subscription;
  private transitionSub: Function;
  private submitSearchAnalytics: (() => void) & Cancelable;

  @ViewChild('signSuccessModal') signSuccessModal: ModalComponent;
  @ViewChild('disputeSuccessModal') disputeSuccessModal: ModalComponent;
  @ViewChild('disputeModal') disputeModal: ModalComponent;
  @ViewChild('termsModal') termsModal: TermsModalComponent;

  reason: string;
  searchText: string | null = null;
  disputeSubmitting = false;

  defaultFilterSortOptions: DefaultTimecardFilterSortOptions = {
    unsigned: {
      query: null,
      filter: '',
      sort: 'date-submitted-desc',
      view: 'unsigned'
    },
    signed: {
      query: null,
      filter: 'all-timecards',
      sort: 'week-end-desc',
      view: 'signed'
    }
  };

  unsignedSortOptions = new Map<string, [string, {}]>([
    ['date-submitted-desc', ['timecards.sort.date-submitted-desc', {}]],
    ['date-submitted-asc', ['timecards.sort.date-submitted-asc', {}]]
  ]);

  signedSortOptions = new Map<string, [string, {}]>([
    ['week-end-desc', ['timecards.sort.week-ending-desc', {}]],
    ['week-end-asc', ['timecards.sort.week-ending-asc', {}]]
  ]);

  signedFilterOptions = new Map<string, [string, {}]>([
    ['all-timecards', ['timecards.filter.all-timecards', {}]],
    ['signed', ['timecards.filter.signed', {}]],
    ['approved', ['timecards.filter.approved', {}]]
  ]);

  countOptions = new Map<number, [string, {}]>([
    [10, ['pagination.count-option', {number: 10}]],
    [25, ['pagination.count-option', {number: 25}]],
    [50, ['pagination.count-option', {number: 50}]],
    [100, ['pagination.count-option', {number: 100}]]
  ]);

  timecards$: Observable<TimecardSelection[]>;
  allTimecards$: Observable<Timecard[]>;
  lowVal$: Observable<number>;
  highVal$: Observable<number>;
  total$: Observable<number>;

  loading$ = new BehaviorSubject(true);
  selected$ = new BehaviorSubject<number[]>([]);
  currentPage$ = new BehaviorSubject(1);
  pageSize$ = new BehaviorSubject(10);
  reload$ = new Subject<void>();
  hasSearch$ = new BehaviorSubject(false);
  filterSortOptions$ = new BehaviorSubject<TimecardFilterSortOptions>(this.defaultFilterSortOptions.unsigned);
  masquerading$ = this.Auth.masquerading$;

  constructor(private route: ActivatedRoute,
              private Navigation: NavigationService,
              private Analytics: AnalyticsService,
              private Api: ClientApiService,
              private Auth: AuthService,
              private filterSortPipe: TimecardFilterSortPipe) {
  }

  ngOnInit() {
    this.setupSearchAnalytics();
    this.initializeFilterSortForView();
    this.setupTimecardSource();
    this.setupTimecardsToDisplay();
    this.setupPagination();
    this.loadTimecardData();
  }

  setupSearchAnalytics() {
    this.submitSearchAnalytics = debounce(() => {
      if (this.searchText) {
        this.Analytics.trackEvent('Timecards: Search', {
          category: 'Action',
          searchText: this.searchText
        });
      }
    }, 2000);
  }

  initializeFilterSortForView() {
    this.clearSearch();
    this.route.paramMap.subscribe(params => {
      this.updateFilterSort(this.defaultFilterSortOptions[params.get('view')]);
      this.currentPage$.next(1);
    });
  }

  updateFilterSort(options: Partial<TimecardFilterSortOptions>) {
    this.filterSortOptions$.next({...this.filterSortOptions$.getValue(), ...options});
  }

  setupTimecardSource() {
    this.allTimecards$ = this.reload$
      .pipe(
        tap(() => this.loading$.next(true)),
        switchMap(() => this.Api.getTimecards(null)),
        tap(() => {
          this.loading$.next(false);
          this.currentPage$.next(1);
        }),
        catchError((err: HttpError) =>
          this.handleError(err, 'Failed to load timecards.')),
        cache()
      );
  }

  setupTimecardsToDisplay() {
    this.timecards$ = combineLatest(
      this.allTimecards$,
      this.selected$,
      this.filterSortOptions$,
      (allTimecards, selected, filterSortOptions) => {
        return this.filterSortPipe.transform(allTimecards, filterSortOptions).map(t => ({
          timecard: t,
          selected: selected.includes(t.timecardId)
        }));
      });
  }

  setupPagination() {
    this.lowVal$ = combineLatest(
      this.currentPage$,
      this.pageSize$,
      (currentPage, pageSize) => ((currentPage - 1) * pageSize) + 1
    );

    this.highVal$ = combineLatest(
      this.currentPage$,
      this.pageSize$,
      this.timecards$,
      (currentPage, pageSize, signedTimecards) => Math.min(signedTimecards.length, currentPage * pageSize)
    );

    this.total$ = this.timecards$.pipe(map(timecards => timecards.length));
  }

  loadTimecardData() {
    this.timecardsSub = this.timecards$.subscribe();
    this.reload$.next();
  }

  ngOnDestroy() {
    if (this.transitionSub) this.transitionSub();
    if (this.timecardsSub) this.timecardsSub.unsubscribe();
    if (this.submitSearchAnalytics) this.submitSearchAnalytics.flush();
  }

  setCount(count: number) {
    this.pageSize$.next(count);
    this.currentPage$.next(1);
  }

  isTimecardSelected(): boolean {
    return this.selected$.getValue().length > 0;
  }

  signTimecardsComplete() {
    this.reload$.next();
    this.selected$.next([]);
  }

  toggleAll(timecards: {timecard: Timecard}[], e: boolean) {
    if (e === true || e === false) {
      this.selected$.next(e ? timecards.map(t => t.timecard.timecardId) : []);
    }
  }

  submitDispute() {
    if (this.masquerading$.getValue()) {
      return;
    }
    const selected = this.selected$.getValue();

    this.Analytics.trackEvent('Timecards: Timecard(s) disputed', {count: selected.length});

    if (selected && selected.length > 0) {
      this.disputeSubmitting = true;
      this.Api.disputeTimecards({ids: selected, reason: this.reason})
        .pipe(tap(() => {
          this.disputeModalClose();
          this.disputeSuccessModal.show();
          setTimeout(() => {
            this.disputeSuccessModal.hide();
          }, 3000);
          this.disputeSubmitting = false;
        }))
        .subscribe(() => {
          this.reload$.next();
          this.selected$.next([]);
        }, err =>
          this.handleError(err, 'Failed to dispute timecards'));
    }
  }

  disputeModalClose() {
    this.reason = null;
    this.disputeModal.hide();
  }

  timecardSelected(id: number, selected: boolean) {
    const selectedIds = new Set(this.selected$.getValue());
    if (selected) {
      selectedIds.add(id);
    } else {
      selectedIds.delete(id);
    }

    this.selected$.next(Array.from(selectedIds));
  }

  showDisputeModal() {
    if (this.selected$.getValue().length > 0) {
      this.disputeModal.show();
    }
  }

  setSort(sort) {
    this.updateFilterSort({sort});
    this.Analytics.trackEvent('Timecards: Sort set to ' + sort);
  }

  setFilter(filter) {
    this.updateFilterSort({filter});
    this.Analytics.trackEvent('Timecards: Filter set to ' + filter);
  }

  searchTextChanged() {
    this.updateFilterSort({query: this.searchText});
    this.hasSearch$.next(!!this.searchText);
    this.submitSearchAnalytics();
  }

  searchKeydown(event: KeyboardEvent) {
    if (event.keyCode === KEY_ESC) {
      this.clearSearch();
      event.preventDefault();
    } else if (event.keyCode === KEY_ENTER) {
      (event.target as HTMLElement).blur();
      event.preventDefault();
    }
  }

  clearSearch() {
    this.searchText = null;
    this.searchTextChanged();
  }

  handleError(err, message) {
    console.error(message, err);
    if (err.status === 401) {
      this.Navigation.handleLogin();
    } else {
      this.Navigation.handleError(err);
    }
    return of([]);
  }
}

interface TimecardSelection {
  timecard: Timecard;
  selected: boolean;
}

interface DefaultTimecardFilterSortOptions {
  unsigned: TimecardFilterSortOptions;
  signed: TimecardFilterSortOptions;
}
